/**
 * WordOpsPanel — debug overlay that streams every Word/Office.js
 * round-trip in real time. Visible only when
 * ``localStorage["asthra:debug:word-ops"] === "1"``.
 *
 * Lets us see exactly when a Word.run fires, which one, how long it
 * took, and what branch it picked. Critical for diagnosing hangs on
 * long CSR drafts: if you press ctrl+A and an entry shows
 * ``readSelectionText`` instead of the cheap
 * ``probeSelectionForChip / multi-paragraph-short-circuit``, the
 * shipping-the-whole-body regression is back.
 */

import { makeStyles, tokens } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useState } from "react";
import { type WordOpLogEntry, clearWordOps, subscribeWordOps } from "../../services/wordOpsLogger";

const DEBUG_FLAG = "asthra:debug:word-ops";
const MAX_VISIBLE = 30;

const useStyles = makeStyles({
  root: {
    position: "fixed",
    bottom: "8px",
    right: "8px",
    width: "360px",
    maxHeight: "40vh",
    overflow: "auto",
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: "6px",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, 'Liberation Mono', monospace",
    fontSize: "10.5px",
    color: tokens.colorNeutralForeground1,
    boxShadow: tokens.shadow8,
    zIndex: 9999,
    padding: "6px 8px",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingBottom: "4px",
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    marginBottom: "4px",
  },
  title: {
    fontWeight: 600,
  },
  actions: {
    display: "flex",
    gap: "6px",
  },
  button: {
    background: "transparent",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    color: tokens.colorNeutralForeground2,
    fontSize: "10px",
    padding: "2px 6px",
    borderRadius: "3px",
    cursor: "pointer",
    fontFamily: "inherit",
  },
  empty: {
    color: tokens.colorNeutralForeground3,
    padding: "4px 0",
  },
  entry: {
    display: "grid",
    gridTemplateColumns: "auto auto 1fr",
    gap: "6px",
    padding: "2px 0",
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    alignItems: "baseline",
  },
  entryName: {
    fontWeight: 500,
  },
  entryDuration: {
    color: tokens.colorNeutralForeground2,
    minWidth: "52px",
    textAlign: "right",
  },
  entrySlow: {
    color: tokens.colorPaletteRedForeground1,
  },
  entryError: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: 600,
  },
  entryDetails: {
    color: tokens.colorNeutralForeground3,
    fontSize: "9.5px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
});

function shouldRender(): boolean {
  try {
    return typeof window !== "undefined" && window.localStorage?.getItem(DEBUG_FLAG) === "1";
  } catch {
    return false;
  }
}

function formatDetails(d: Record<string, unknown> | undefined): string {
  if (!d) return "";
  try {
    return Object.entries(d)
      .map(([k, v]) => `${k}=${typeof v === "string" ? v : JSON.stringify(v)}`)
      .join(" ");
  } catch {
    return "";
  }
}

export const WordOpsPanel: React.FC = () => {
  const styles = useStyles();
  const [entries, setEntries] = useState<readonly WordOpLogEntry[]>([]);
  const [enabled] = useState<boolean>(() => shouldRender());

  useEffect(() => {
    if (!enabled) return;
    return subscribeWordOps((next) => setEntries(next));
  }, [enabled]);

  if (!enabled) return null;

  const visible = entries.slice(-MAX_VISIBLE).slice().reverse();

  return (
    <div className={styles.root} role="log" aria-label="Word ops debug log">
      <div className={styles.header}>
        <span className={styles.title}>Word ops</span>
        <div className={styles.actions}>
          <button type="button" className={styles.button} onClick={() => clearWordOps()}>
            clear
          </button>
        </div>
      </div>
      {visible.length === 0 && <div className={styles.empty}>no Word ops yet</div>}
      {visible.map((e, i) => {
        const slow = e.durationMs >= 200;
        return (
          <div
            key={`${e.ts}-${i}`}
            className={styles.entry}
            title={`${new Date(e.ts).toISOString()} · ${e.name} · ${e.durationMs} ms`}
          >
            <span className={e.error ? styles.entryError : slow ? styles.entrySlow : styles.entryName}>{e.name}</span>
            <span className={e.error || slow ? styles.entrySlow : styles.entryDuration}>
              {e.durationMs.toFixed(1)}ms
            </span>
            <span className={styles.entryDetails}>{formatDetails(e.details)}</span>
          </div>
        );
      })}
    </div>
  );
};
